import { Children, cloneElement, isValidElement } from 'react'
import { cn } from '@/shared/lib/cn'

interface MarqueeRowProps {
  children: React.ReactNode
  className?: string
  /** Animation duration. Lower = faster. Default 40s. */
  durationSec?: number
  /** Pause animation on hover (still respects reduced-motion). */
  pauseOnHover?: boolean
}

/**
 * Endless horizontal marquee (§6.7.7). RTL-aware: the keyframe direction
 * flips automatically via `html[dir='rtl'] .marquee-track`.
 *
 * Children are rendered TWICE so the loop is seamless. Mark the duplicate
 * with aria-hidden to keep AT clean.
 */
export function MarqueeRow({
  children,
  className,
  durationSec = 40,
  pauseOnHover = true,
}: MarqueeRowProps) {
  const items = Children.toArray(children)
  return (
    <div
      className={cn(
        'marquee relative w-full overflow-hidden mask-edges-x',
        pauseOnHover && '[&:hover_.marquee-track]:[animation-play-state:paused]',
        className,
      )}
    >
      <div
        className="marquee-track"
        style={{ animationDuration: `${durationSec}s` }}
      >
        {items.map((child, i) => (
          <div key={`a-${i}`} className="shrink-0">
            {child}
          </div>
        ))}
        {items.map((child, i) => {
          const dup =
            isValidElement(child) && (child as React.ReactElement).type
              ? cloneElement(child as React.ReactElement<{ 'aria-hidden'?: boolean }>, { 'aria-hidden': true })
              : child
          return (
            <div key={`b-${i}`} className="shrink-0" aria-hidden>
              {dup}
            </div>
          )
        })}
      </div>
    </div>
  )
}
